import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ArrowRight, Star, TrendingUp, Users, Calendar, MapPin, Quote, Clock, MessageSquare, Zap, Target, CheckCircle, BarChart3 } from 'lucide-react';

const SuccessStories = () => {
  const location = useLocation();

  // Handle smooth scrolling to case study when navigated from Home page
  useEffect(() => {
    if (location.state?.scrollToId) {
      // Wait for the component to fully render before scrolling
      setTimeout(() => {
        const element = document.getElementById(location.state.scrollToId);
        if (element) {
          element.scrollIntoView({ 
            behavior: 'smooth',
            block: 'start',
            inline: 'nearest'
          });
        }
      }, 100);
    }
  }, [location.state]);

  const handleBookDemo = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleStartTrial = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const featuredCaseStudies = [
    {
      id: 'american-pizzeria',
      restaurant: 'American Pizzeria',
      location: 'Punjab, India',
      type: 'Pizza Chain',
      timeframe: '3 months',
      challenge: `New restaurant in competitive market. Zero online presence. Competing against 15 established pizza chains within 2 miles. No website, poor Google visibility, only 3 reviews. Owners Rajesh and Priya Kumar had incredible food but couldn't get customers in the door. Weekend nights were 40% empty despite rave reviews from those who found them.`,
      implementation: {
        month1: 'Launched premium website with interactive menu, professional food photography, online booking integration. Isabella AI went live handling reservations and answering questions about toppings, sizes, dietary options, delivery areas.',
        month2: 'Activated Review Accelerator. Started collecting 5-star reviews from happy diners. Optimized Google Business Profile with posts, photos, and Q&A.',
        month3: 'Local SEO kicked in. Created content targeting "best pizza in Punjab," "best paneer tikka pizza near me," and "best garlic bread near me."'
      },
      results: {
        reservations: '47/week (up from 12)—292% increase',
        reviews: '63 new reviews averaging 4.8★',
        rankings: 'First page ranking for "best paneer tikka pizza"',
        isabella: '156 total, 38 after-hours reservations captured',
        revenue: '$18,000 estimated additional monthly revenue',
        status: 'Now taking waitlist reservations—fully booked Friday-Sunday'
      },
      quote: 'We were invisible online. PrismoSpark changed everything. Isabella handles late-night reservation requests and answers questions about ingredients in Hindi and Punjabi—customers love it. We\'re now booked solid weekends and turning people away. The review system alone paid for itself in month two.',
      owner: 'Rajesh Kumar, Owner',
      takeaway: 'American Pizzeria went from struggling new restaurant to neighborhood favorite in 90 days. The combination of professional web presence, 24/7 AI booking, and systematic review collection created a growth flywheel that compounds monthly.'
    },
    {
      id: 'madras-leaf',
      restaurant: 'Madras Leaf',
      location: 'Punjab, India', 
      type: 'South Indian Restaurant',
      timeframe: '4 months',
      challenge: `8-year-old restaurant with declining revenue. Website built in 2016, barely functional on mobile. 47 Google reviews at 3.9★ with recent negative reviews unanswered. Lunch reservations down 40% year-over-year. General Manager Suraj knew they had a reputation problem but didn't know how to fix it. Competitors with newer restaurants and better online presence were stealing customers.`,
      implementation: {
        month1: 'Complete website rebuild. Mobile-first design, improved menu presentation, professional food photography, streamlined booking.',
        month2: 'Isabella AI integration. Started capturing missed opportunities—people calling after hours, asking about daily specials, dietary accommodations.',
        month3: 'Aggressive reputation recovery. Responded professionally to all negative reviews. Activated Review Accelerator to generate new positive reviews. Within 60 days, had 45 new 5-star reviews pushing old negative reviews down.'
      },
      results: {
        rating: '4.6★ with 89 new positive reviews (from 3.9★)',
        bookings: 'Lunch Bookings: Up 65% vs. previous year',
        rankings: 'First page ranking for "best dosa near me" and "best south indian restaurant near me"',
        mobile: 'Mobile Traffic: 87% (vs. 43% before—traffic more than doubled)',
        isabella: '312 conversations, 78% successfully answered questions about daily specials',
        revenue: 'Lunch revenue exceeded previous year by 65%'
      },
      quote: 'We were drowning. Bad reviews piled up, our website looked ancient, and lunch service was dying. PrismoSpark saved us. The new website looks incredible, but the reputation management was the game-changer. We went from defensive to thriving in six months. Isabella answering questions about our rotating menu freed up our host stand during rush times.',
      owner: 'Suraj, General Manager',
      takeaway: 'Madras Leaf proves it\'s never too late to recover. Even restaurants with reputation damage can rebuild with the right system. Systematic review generation + professional responses + modern web presence = complete turnaround.'
    }
  ];

  const quickWins = [
    {
      restaurant: 'Mario\'s Pizzeria',
      location: 'Khanna, India',
      challenge: 'Traditional pizzeria with no online ordering, limited reviews',
      results: '43 new 5-star reviews in 2 months, monthly revenue up 32%, Isabella handles phone overflow during Friday dinner rush',
      timeframe: '3 months',
      color: 'from-[#D4623C] to-[#B8532F]'
    },
    {
      restaurant: 'Mumbai Spice House',
      location: 'Ludhiana, India',
      challenge: 'Serving Punjabi community but phone-only reservations, language barrier with younger tech-savvy customers',
      results: 'Isabella handles Hindi/Punjabi perfectly, 89 chatbot reservations first month, 24% increase in millennial customers',
      timeframe: '2 months',
      color: 'from-[#E6B84D] to-[#D4A935]'
    },
    {
      restaurant: 'Green Leaf Café',
      location: 'Delhi, India',
      challenge: 'Healthy café buried in search results despite great food and loyal customers',
      results: 'From page 3 to first page on Google in their neighborhood, website traffic up 340%, lunch crowd expanded beyond regulars',
      timeframe: '4 months',
      color: 'from-[#2D6A4F] to-[#1F4A36]'
    },
    {
      restaurant: 'Bay Cafe',
      location: 'Amritsar, India',
      challenge: 'Missing late-night college student crowd despite being open until midnight',
      results: 'Isabella captures after-hours orders, weekend bookings up 23%, became go-to spot for late-night dining',
      timeframe: '2 months',
      color: 'from-[#0A7A6E] to-[#085A54]'
    }
  ];

  const growthPattern = [
    { phase: 'Week 1-3', description: 'New web presence attracts curiosity traffic', icon: Target },
    { phase: 'Month 1-2', description: 'Isabella captures missed opportunities (after-hours, FAQs, multilingual)', icon: MessageSquare },
    { phase: 'Month 2-4', description: 'Review velocity increases, ratings improve, social proof builds', icon: Star },
    { phase: 'Month 3-6', description: 'SEO momentum builds, "near me" visibility grows', icon: TrendingUp },
    { phase: 'Month 6+', description: 'Growth compounds—more reviews → higher rankings → more visibility → more customers → more reviews', icon: BarChart3 }
  ];

  return (
    <div className="pt-16 overflow-hidden">
      {/* Hero Section */}
      <section className="relative min-h-screen bg-gradient-to-br from-brand-charcoal via-brand-charcoal-700 to-brand-charcoal">
        {/* Background Elements */}
        <div className="absolute inset-0">
          <div className="absolute top-20 right-20 w-64 h-64 bg-gradient-to-br from-[#2D6A4F]/20 to-[#2D6A4F]/20 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 left-20 w-64 h-64 bg-gradient-to-br from-brand-sienna/20 to-brand-gold/20 rounded-full blur-3xl animate-pulse delay-1000"></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-48 h-48 bg-gradient-to-br from-[#2D6A4F]/20 to-[#2D6A4F]/20 rounded-full blur-3xl animate-pulse delay-500"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-32 text-center">
          <h1 className="text-5xl lg:text-7xl font-bold text-white mb-8 leading-tight">
            Real Restaurants. Real
            <span className="block bg-gradient-to-r from-[#2D6A4F] to-[#2D6A4F] bg-clip-text text-transparent animate-gradient bg-300%">
              Results.
            </span>
          </h1>
          <p className="text-xl text-brand-ivory/90 max-w-4xl mx-auto mb-16 leading-relaxed">
            These aren't cherry-picked success stories. These are typical results from restaurants that commit to the system.
          </p>
          
          {/* Quick Stats */}
          <div className="grid md:grid-cols-4 gap-8 max-w-5xl mx-auto">
            {[
              { number: "292%", label: "Avg Reservation Increase", icon: Calendar },
              { number: "4.8★", label: "Avg Review Rating", icon: Star },
              { number: "$18K", label: "Monthly Revenue Boost", icon: TrendingUp },
              { number: "90", label: "Days to Full Growth", icon: Clock }
            ].map((stat, index) => (
              <Card key={index} className="bg-white/10 backdrop-blur-md border border-white/20 hover:bg-white/20 transition-all duration-500 transform hover:-translate-y-4">
                <CardHeader className="text-center pb-4">
                  <div className="w-16 h-16 bg-gradient-to-br from-[#2D6A4F] to-[#2D6A4F] rounded-2xl flex items-center justify-center mx-auto mb-4 transform group-hover:scale-110 group-hover:rotate-6 transition-all duration-500 shadow-lg">
                    <stat.icon className="text-white" size={28} />
                  </div>
                  <CardTitle className="text-3xl font-bold text-white mb-2">{stat.number}</CardTitle>
                  <CardDescription className="text-brand-ivory/80 font-medium">{stat.label}</CardDescription>
                </CardHeader>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Case Studies */}
      <section className="py-32 bg-gradient-to-br from-brand-ivory via-brand-ivory-50 to-brand-ivory">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <h2 className="text-4xl lg:text-6xl font-bold text-brand-charcoal mb-8">
              Featured Case Studies
            </h2>
            <p className="text-xl text-brand-charcoal-600 max-w-3xl mx-auto">
              Deep-dive into how real restaurants transformed their business with PrismoSpark
            </p>
          </div>

          {featuredCaseStudies.map((study, index) => (
            <div key={study.id} id={study.id} className="mb-32">
              <Card className="overflow-hidden shadow-2xl border-0 bg-white">
                <div className={`grid lg:grid-cols-2 ${index % 2 === 1 ? 'lg:grid-flow-col-dense' : ''}`}>
                  {/* Content Side */}
                  <div className={`p-8 lg:p-12 ${index % 2 === 1 ? 'lg:col-start-2' : ''}`}>
                    <div className="space-y-6">
                      {/* Header */}
                      <div>
                        <div className="flex items-center space-x-3 mb-4">
                          <MapPin className="text-brand-charcoal-600" size={20} />
                          <span className="text-brand-charcoal-600 font-medium">{study.location}</span>
                          <Badge variant="outline" className="border-brand-charcoal/30 text-brand-charcoal">{study.type}</Badge>
                        </div>
                        <h3 className="text-3xl lg:text-4xl font-bold text-brand-charcoal mb-4">{study.restaurant}</h3>
                        <div className="flex items-center space-x-4 text-sm text-brand-charcoal-600">
                          <span>Time with PrismoSpark: <strong>{study.timeframe}</strong></span>
                        </div>
                      </div>

                      {/* Challenge */}
                      <div>
                        <h4 className="text-xl font-bold text-brand-charcoal mb-4 flex items-center">
                          <Target className="mr-2 text-brand-sienna" size={20} />
                          The Challenge
                        </h4>
                        <p className="text-brand-charcoal-600 leading-relaxed">{study.challenge}</p>
                      </div>

                      {/* Implementation */}
                      <div>
                        <h4 className="text-xl font-bold text-brand-charcoal mb-4 flex items-center">
                          <Zap className="mr-2 text-[#2D6A4F]" size={20} />
                          What We Built
                        </h4>
                        <div className="space-y-3">
                          <div>
                            <span className="font-semibold text-[#2D6A4F]">Month 1:</span> {study.implementation.month1}
                          </div>
                          <div>
                            <span className="font-semibold text-brand-sienna">Month 2:</span> {study.implementation.month2}
                          </div>
                          <div>
                            <span className="font-semibold text-[#2D6A4F]">Month 3:</span> {study.implementation.month3}
                          </div>
                        </div>
                      </div>

                      {/* Quote */}
                      <div className="bg-brand-ivory-50 p-6 rounded-xl">
                        <Quote className="text-brand-charcoal/40 mb-4" size={32} />
                        <p className="text-brand-charcoal-600 italic leading-relaxed text-lg mb-4">"{study.quote}"</p>
                        <div className="text-sm text-brand-charcoal-600 font-medium">— {study.owner}</div>
                      </div>

                      {/* Key Takeaway */}
                      <div className="bg-gradient-to-r from-[#2D6A4F]/10 to-[#2D6A4F]/20 p-6 rounded-xl border-l-4 border-[#2D6A4F]">
                        <h5 className="font-bold text-brand-charcoal mb-2 flex items-center">
                          <CheckCircle className="mr-2 text-[#2D6A4F]" size={20} />
                          Key Takeaway
                        </h5>
                        <p className="text-brand-charcoal-600 leading-relaxed">{study.takeaway}</p>
                      </div>
                    </div>
                  </div>

                  {/* Results Side */}
                  <div className={`bg-gradient-to-br from-brand-charcoal to-brand-charcoal-700 p-8 lg:p-12 text-white ${index % 2 === 1 ? 'lg:col-start-1' : ''}`}>
                  <h4 className="text-2xl font-bold mb-8 flex items-center">
                    <BarChart3 className="mr-3 text-[#2D6A4F]" size={28} />
                      Results After 90 Days
                    </h4>
                    
                    <div className="space-y-6">
                      {Object.entries(study.results).map(([key, value], idx) => (
                        <div key={idx} className="flex items-start space-x-4 p-4 bg-white/10 backdrop-blur-sm rounded-lg border border-white/20">
                          <div className="w-2 h-2 bg-[#2D6A4F] rounded-full mt-3 flex-shrink-0"></div>
                          <div>
                            <div className="font-semibold text-[#2D6A4F] text-sm uppercase tracking-wide mb-1">
                              {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                            </div>
                            <div className="text-white font-medium">{value}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </Card>
            </div>
          ))}
        </div>
      </section>

      {/* More Success Stories */}
      <section className="py-32 bg-brand-ivory-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-6xl font-bold text-brand-charcoal mb-8">
              More Success Stories
            </h2>
            <p className="text-xl text-brand-charcoal-600 max-w-3xl mx-auto">
              Quick wins and transformations across different restaurant types
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {quickWins.map((story, index) => (
              <Card key={index} className="group border-0 shadow-xl hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 bg-white overflow-hidden">
                <div className={`h-2 bg-gradient-to-r ${story.color}`}></div>
                <CardHeader className="pb-6">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <CardTitle className="text-2xl text-brand-charcoal group-hover:bg-gradient-to-r group-hover:from-brand-charcoal-600 group-hover:to-brand-charcoal group-hover:bg-clip-text group-hover:text-transparent transition-all duration-300">
                        {story.restaurant}
                      </CardTitle>
                      <div className="flex items-center space-x-2 text-brand-charcoal-600 mt-2">
                        <MapPin size={16} />
                        <span className="text-sm">{story.location}</span>
                      </div>
                    </div>
                    <Badge className={`bg-gradient-to-r ${story.color} text-white border-0 px-3 py-1`}>
                      {story.timeframe}
                    </Badge>
                  </div>
                  
                  <div className="space-y-4">
                    <div>
                      <h4 className="font-semibold text-brand-charcoal mb-2 flex items-center">
                        <Target className="mr-2 text-brand-sienna" size={16} />
                        Challenge:
                      </h4>
                      <p className="text-brand-charcoal-600 text-sm leading-relaxed">{story.challenge}</p>
                    </div>
                    
                    <div>
                      <h4 className="font-semibold text-brand-charcoal mb-2 flex items-center">
                        <CheckCircle className="mr-2 text-[#2D6A4F]" size={16} />
                        Results:
                      </h4>
                      <p className="text-brand-charcoal-600 text-sm leading-relaxed font-medium">{story.results}</p>
                    </div>
                  </div>
                </CardHeader>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* The Pattern */}
      <section className="py-32 bg-gradient-to-r from-brand-charcoal to-brand-charcoal-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-6xl font-bold text-white mb-8">
              The Pattern
            </h2>
            <p className="text-xl text-brand-ivory/90 max-w-4xl mx-auto mb-12">
              Every successful restaurant follows the same growth sequence:
            </p>
          </div>

          <div className="grid md:grid-cols-5 gap-6">
            {growthPattern.map((phase, index) => (
              <Card key={index} className="bg-white/10 backdrop-blur-md border border-white/20 hover:bg-white/20 transition-all duration-500 transform hover:-translate-y-4 group">
                <CardHeader className="text-center pb-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-[#2D6A4F] to-[#2D6A4F] rounded-2xl flex items-center justify-center mx-auto mb-4 transform group-hover:scale-110 group-hover:rotate-6 transition-all duration-500 shadow-lg">
                    <phase.icon className="text-white" size={24} />
                  </div>
                  <CardTitle className="text-lg font-bold text-white mb-3">{phase.phase}</CardTitle>
                  <CardDescription className="text-brand-ivory/80 text-sm leading-relaxed">{phase.description}</CardDescription>
                </CardHeader>
              </Card>
            ))}
          </div>

          <div className="text-center mt-16">
            <p className="text-xl text-brand-ivory/90 max-w-4xl mx-auto mb-8 leading-relaxed">
              <strong className="text-white">Month 6+:</strong> Growth compounds—more reviews → higher rankings → more visibility → more customers → more reviews
            </p>
            
            <Button 
              onClick={handleStartTrial}
              className="group relative bg-[#D4623C] text-white hover:bg-[#D4623C] font-bold text-xl px-12 py-6 rounded-2xl shadow-2xl transition-all duration-300 transform hover:-translate-y-1 hover:scale-[1.02] overflow-hidden"
            >
              <div className="relative z-10 flex items-center justify-center space-x-3">
                <Zap className="w-6 h-6" />
                <span>Start Your 30-Day Trial</span>
                <ArrowRight className="w-5 h-5" />
              </div>
            </Button>
          </div>
        </div>
      </section>

      {/* Contact Section Placeholder for Scroll Target */}
      <section id="contact" className="py-20 bg-gradient-to-br from-brand-ivory-50 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-5xl font-bold text-brand-charcoal mb-8">
            Ready to Create Your Success Story?
          </h2>
          <p className="text-xl text-brand-charcoal-600 mb-8 leading-relaxed">
            Contact us to start your transformation today.
          </p>
          <div className="bg-white p-8 rounded-2xl shadow-2xl border border-brand-charcoal/20">
            <p className="text-lg text-brand-charcoal-600 mb-4">
              📞 Phone: <span className="font-semibold">(555) 123-GROW</span>
            </p>
            <p className="text-lg text-brand-charcoal-600 mb-4">
              ✉️ Email: <span className="font-semibold">hello@prismospark.com</span>
            </p>
            <p className="text-base text-brand-charcoal-500">
              Our team is available Monday-Friday, 9 AM - 6 PM EST
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SuccessStories;